// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as api from '../../api/client'
import ReplayPanel from './ReplayPanel'
import MentionTextarea from '../../components/workflow/MentionTextarea'
import { friendlyError } from '../../api/errors'
import { useAuth } from '../../auth/AuthContext'
import { ErrorBanner, LoadingState, relativeTime } from '../../components/workflow/StatePills'
import { SeverityBadge, StatusBadge } from './supportBadges'

// D-SUPPORT-2 Phase A transitions. Buttons offer the USEFUL moves, not the full matrix
// (the backend allows any active<->active move). Legacy statuses map to an escape path.
const NEXT: Record<api.SupportStatus, api.SupportStatus[]> = {
  new: ['open', 'pending', 'solved'],
  open: ['pending', 'on_hold', 'solved'],
  pending: ['open', 'on_hold', 'solved'],
  on_hold: ['open', 'pending', 'solved'],
  solved: ['closed', 'open'],
  closed: ['open'],
  // legacy rows created under the phase-1 model
  triage: ['open', 'pending', 'solved'],
  in_progress: ['pending', 'on_hold', 'solved'],
  resolved: ['closed', 'open'],
}
const STATUS_BTN_LABELS: Partial<Record<api.SupportStatus, string>> = {
  open: 'Open', pending: 'Pending (waiting on customer)', on_hold: 'On hold', solved: 'Solved', closed: 'Closed',
}
const SEVERITIES: api.SupportSeverity[] = ['low', 'normal', 'high', 'critical']

// D-SUP-2: work controls (assign / status / severity / internal notes) exist ONLY on the
// SaaS admin surface. The pharmacy surface NEVER renders them, regardless of permissions —
// the backend also rejects work ops from pharmacy-bound actors (403 platform_only).
export default function SupportTicketDetailPage({ surface = 'pharmacy' }: { surface?: 'pharmacy' | 'admin' }) {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { hasPermission, me } = useAuth()
  const isAdminSurface = surface === 'admin'
  const canWork = isAdminSurface && hasPermission('support.tickets.work')
  const canManage = isAdminSurface && hasPermission('support.tickets.manage')

  const [data, setData] = useState<{ ticket: api.SupportTicketDetail; comments: api.SupportTicketComment[]; events: api.SupportTicketEvent[]; attachments: api.SupportTicketAttachment[]; tags?: string[]; sla?: api.SupportSlaDetail | null; close_request?: api.SupportCloseRequest } | null>(null)
  const [newTag, setNewTag] = useState('')
  const [closeNote, setCloseNote] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [comment, setComment] = useState('')
  // D-SUPPORT-12 @mentions. `picked` is the identity that actually travels to the server; the
  // "@Name" in the text is only what humans read. Keeping them separate is what makes a rename or
  // a duplicate display name harmless.
  const [people, setPeople] = useState<api.SupportMentionablePerson[]>([])
  const [picked, setPicked] = useState<api.MentionUser[]>([])
  // D-SUPPORT-4 W3: macros + AI draft feed the composer; the agent always reviews before sending.
  const [macros, setMacros] = useState<api.SupportMacro[]>([])
  const [drafting, setDrafting] = useState(false)
  useEffect(() => {
    if (isAdminSurface) api.listSupportMacros().then(r => setMacros(r.items)).catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminSurface])
  const [internal, setInternal] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    if (!id) return
    setLoading(true)
    api.getSupportTicket(id)
      .then(setData)
      .catch(e => setError(friendlyError(e)))
      .finally(() => setLoading(false))
  }, [id])
  useEffect(load, [load])

  // setBusy(false) belongs in finally, not only in catch. Resetting it solely on the error
  // path meant every SUCCESSFUL action left busy=true forever: change the status once and
  // every control on the page stayed disabled until a reload.
  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setError(null)
    try { await fn(); load() }
    catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }
  useEffect(() => {
    // Non-fatal: the composer still works without tagging, but say so rather than showing an
    // empty picker that looks like "nobody can be tagged".
    api.listSupportTicketMentionable(id).then(r => setPeople(r.people)).catch(() => setPeople([]))
  }, [id])

  const postComment = async () => {
    if (!comment.trim()) return
    // Only send tags whose "@Name" is still in the text — deleting the text should delete the tag,
    // otherwise you can silently notify someone whose name you removed.
    const live = picked.filter(p => comment.includes('@' + p.display_name))
    await act(async () => {
      await api.addSupportTicketComment(id, comment.trim(), internal, live.map(p => p.id))
      setComment(''); setInternal(false); setPicked([])
    })
  }
  // Tracked separately from `busy`: the attachment control's label is driven by this, and
  // hanging it off the shared flag made it read "Uploading…" after ANY action on the page —
  // posting a comment, changing status — when no upload had been started.
  const [uploading, setUploading] = useState(false)
  const uploadFile = async (file: File) => {
    setUploading(true)
    try { await act(async () => { await api.uploadSupportTicketAttachment(id, file) }) }
    finally { setUploading(false) }
  }
  const downloadAttachment = async (attId: string) => {
    try { const r = await api.getSupportTicketAttachmentUrl(id, attId); window.open(r.url, '_blank', 'noopener') }
    catch (e) { setError(friendlyError(e)) }
  }
  const fmtSize = (n: number) => n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`

  if (loading) return <div style={wrap}><LoadingState label="Loading ticket…" /></div>
  if (!data) return <div style={wrap}>{error && <ErrorBanner message={error} onClose={() => setError(null)} />}</div>
  const t = data.ticket

  return (
    <div style={wrap}>
      <button onClick={() => navigate(-1)} style={backBtn}>← Back</button>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
        <h1 style={{ fontSize: 21, fontWeight: 700, margin: 0 }}>#{t.ticket_no} · {t.subject}</h1>
        <SeverityBadge severity={t.severity} />
        <StatusBadge status={t.status} />
      </header>
      <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginBottom: 16 }}>
        {t.category} · opened {relativeTime(t.created_at)} · updated {relativeTime(t.last_activity_at)}
        {(data as any)?.requester && (
          <> · reported by <b style={{ color: 'var(--color-text)' }}>{(data as any).requester.name}</b>
            {' '}({(data as any).requester.pharmacy_name}
            {(data as any).requester.email ? ` · ${(data as any).requester.email}` : ''})</>
        )}
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {/* D-SUPPORT-5 — the customer asks, an agent decides. Rendered on BOTH
          surfaces from one place so the two halves cannot drift apart: the
          pharmacy sees the ask, the agent sees the decision. */}
      <CloseRequestCard
        cr={data.close_request}
        status={t.status}
        isAdminSurface={isAdminSurface}
        canWork={canWork}
        busy={busy}
        note={closeNote}
        setNote={setCloseNote}
        onRequest={() => act(() => api.requestSupportTicketClose(id, closeNote || undefined))}
        onWithdraw={() => act(() => api.withdrawSupportTicketClose(id))}
        onDecline={() => act(() => api.declineSupportTicketClose(id, closeNote || undefined))}
      />

      {canWork && (
        <div style={{ ...card, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={ctrlLbl}>Move to</span>
            {(NEXT[t.status] ?? ['open']).map(s => (
              <button key={s} disabled={busy} onClick={() => act(() => api.setSupportTicketStatus(id, s))} style={ctrlBtn}>
                {STATUS_BTN_LABELS[s] ?? s.replace('_', ' ')}
              </button>
            ))}
          </div>
          {canManage && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={ctrlLbl}>Severity</span>
              <select value={t.severity} disabled={busy} onChange={e => act(() => api.setSupportTicketSeverity(id, e.target.value as api.SupportSeverity))} style={sel}>
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
          <button disabled={busy}
            onClick={() => act(() => api.assignSupportTicket(id, t.assigned_to_account_id ? null : (me?.account_id ?? null)).then(() => api.getSupportTicket(id)))}
            style={ctrlBtn}>
            {t.assigned_to_account_id ? 'Unassign' : 'Assign to me'}
          </button>
        </div>
      )}

      {canWork && data.sla && (
        <div style={{ ...card, display: 'flex', gap: 24, flexWrap: 'wrap' }}>
          <SlaClock label={`First response (${data.sla.priority})`} clock={data.sla.first_response} />
          <SlaClock label="Resolution" clock={data.sla.resolution} />
          {/* Only when a reply is actually owed. Rendering it always would show a permanent dash,
              which reads as "met" rather than "not applicable". */}
          {data.sla.next_response && (
            <SlaClock label="Reply to customer" clock={data.sla.next_response} />
          )}
        </div>
      )}

      {/* Tags: shown to both surfaces (they classify the case, non-PHI slugs);
          editable only from the admin surface with support.tags.manage. */}
      {((data.tags?.length ?? 0) > 0 || (isAdminSurface && hasPermission('support.tags.manage'))) && (
        <div style={card}>
          <div style={sectionLbl}>Tags</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            {(data.tags ?? []).map(tg => (
              <span key={tg} style={{ fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 999, background: '#eef2ff', color: '#4338ca', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                {tg}
                {isAdminSurface && hasPermission('support.tags.manage') && (
                  <button disabled={busy} onClick={() => act(() => api.removeSupportTicketTag(id, tg))}
                    style={{ background: 'none', border: 'none', color: '#4338ca', cursor: 'pointer', padding: 0, fontSize: 13, lineHeight: 1 }}
                    title={`Remove ${tg}`}>×</button>
                )}
              </span>
            ))}
            {isAdminSurface && hasPermission('support.tags.manage') && (
              <span style={{ display: 'inline-flex', gap: 5 }}>
                <input value={newTag} onChange={e => setNewTag(e.target.value)} placeholder="add tag…"
                  onKeyDown={e => { if (e.key === 'Enter' && newTag.trim()) act(async () => { await api.addSupportTicketTag(id, newTag.trim()); setNewTag('') }) }}
                  style={{ padding: '4px 9px', borderRadius: 999, border: '1px dashed var(--color-border)', fontSize: 12, width: 110, background: 'var(--color-bg)', color: 'var(--color-text)' }} />
              </span>
            )}
          </div>
        </div>
      )}

      <div style={card}>
        <div style={sectionLbl}>Description</div>
        <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, lineHeight: 1.55 }}>{t.body}</div>
      </div>

      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={sectionLbl}>Attachments</div>
          <label style={{ ...ctrlBtn, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
            {uploading ? 'Uploading…' : '+ Add file'}
            <input type="file" style={{ display: 'none' }} disabled={busy}
              onChange={e => { const f = e.target.files?.[0]; if (f) uploadFile(f); e.currentTarget.value = '' }} />
          </label>
        </div>
        {data.attachments.length === 0 ? (
          <div style={{ color: 'var(--color-text-mute)', fontSize: 13 }}>No attachments. Max 25 MB per file.</div>
        ) : data.attachments.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '1px solid var(--color-border)' }}>
            <button onClick={() => downloadAttachment(a.id)} style={{ background: 'none', border: 'none', color: 'var(--color-primary,#2563eb)', cursor: 'pointer', fontWeight: 600, fontSize: 13.5, padding: 0, textAlign: 'left', flex: 1 }}>
              {a.file_name}
            </button>
            <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{fmtSize(a.size_bytes)}</span>
          </div>
        ))}
      </div>

      <div style={card}>
        <div style={sectionLbl}>Conversation</div>
        {data.comments.length === 0 && <div style={{ color: 'var(--color-text-mute)', fontSize: 13 }}>No replies yet.</div>}
        {data.comments.map(c => (
          <div key={c.id} style={{ padding: '10px 0', borderTop: '1px solid var(--color-border)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
              <span style={{ fontSize: 12.5, fontWeight: 600 }}>{c.author_name || (c.is_internal ? 'Internal note' : 'Reply')}</span>
              <span style={{ fontSize: 12, color: 'var(--color-text-mute)' }}>{relativeTime(c.created_at)}</span>
              {c.is_internal && <span style={{ ...tag, background: '#fef3c7', color: '#b45309' }}>internal</span>}
            </div>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13.5 }}>{renderWithMentions(c.body, c.mentions)}</div>
            {(c.mentions?.length ?? 0) > 0 && (
              <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 4 }}>
                Tagged: {c.mentions!.map(m => m.display_name ?? 'someone').join(', ')}
              </div>
            )}
          </div>
        ))}
        <div style={{ marginTop: 12 }}>
          {canWork && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              {macros.length > 0 && (
                <select value="" onChange={e => { const m = macros.find(x => x.id === e.target.value); if (m) setComment(c => (c ? c + '\n\n' : '') + m.body) }}
                        style={{ padding: '6px 9px', borderRadius: 7, border: '1px solid var(--color-border)', fontSize: 12.5, background: 'var(--color-bg)', color: 'var(--color-text)' }}>
                  <option value="">Insert macro…</option>
                  {macros.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
                </select>
              )}
              <button type="button" disabled={drafting}
                onClick={() => { setDrafting(true); api.supportAiDraft(id).then(r => setComment(c => (c ? c + '\n\n' : '') + r.draft)).catch(() => {}).finally(() => setDrafting(false)) }}
                style={{ padding: '6px 11px', borderRadius: 7, border: '1px solid var(--color-border)', fontSize: 12.5, background: 'var(--color-bg)', color: 'var(--color-text)', cursor: 'pointer' }}>
                {drafting ? 'Drafting…' : '✨ AI draft'}
              </button>
            </div>
          )}
          {/* L-067: the SHARED mention composer, not a support-only copy. `search` scopes the
              candidates to this ticket (Owl staff + this customer's users, resolved server-side
              from the ticket id); `disabledFor` refuses customers on an internal note WITH the
              reason visible instead of quietly omitting them. */}
          <MentionTextarea
            value={comment}
            onChange={setComment}
            rows={4}
            placeholder="Write a reply…  type @ to tag someone"
            hint="Tag support or this customer"
            mentions={picked}
            setMentions={setPicked}
            search={async q => people
              .filter(p => p.name.toLowerCase().includes(q.toLowerCase()) || (p.email ?? '').toLowerCase().includes(q.toLowerCase()))
              .slice(0, 8)
              .map(p => ({ id: p.account_id, display_name: p.name, email: p.email ?? '' }))}
            disabledFor={u => {
              const p = people.find(x => x.account_id === u.id)
              return internal && p?.is_customer ? 'not on an internal note' : null
            }}
          />
          {picked.filter(p => comment.includes('@' + p.display_name)).length > 0 && (
            <div style={{ marginTop: 6, fontSize: 12.5, color: 'var(--color-text-mute)' }}>
              Will notify: {picked.filter(p => comment.includes('@' + p.display_name)).map(p => p.display_name).join(', ')}
              {internal && picked.some(p => comment.includes('@' + p.display_name)
                                            && people.find(x => x.account_id === p.id)?.is_customer) && (
                <span style={{ color: '#b91c1c' }}>
                  {' '}— remove the customer tags or post this as a public reply.
                </span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
            <button disabled={busy || !comment.trim()} onClick={postComment} style={primaryBtn}>Send</button>
            {canWork && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
                <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} /> Internal note (hidden from pharmacy)
              </label>
            )}
          </div>
        </div>
      </div>

      {canWork && (
      <>
      <div style={card}>
        <div style={sectionLbl}>History</div>
        {data.events.map((e, i) => (
          <div key={i} style={{ fontSize: 12.5, color: 'var(--color-text-soft)', padding: '3px 0' }}>
            {relativeTime(e.created_at)} · {e.actor_name ? `${e.actor_name} ` : ''}{e.event_type.replace('_', ' ')}
            {e.from_value || e.to_value ? `: ${e.from_value ?? '—'} → ${e.to_value ?? '—'}` : ''}
          </div>
        ))}
      </div>

      <ReplayPanel ticketId={id!} />
      </>
      )}
    </div>
  )
}

const SLA_TONE: Record<string, string> = {
  breached: '#b91c1c', missed: '#b91c1c', due_soon: '#b45309',
  ok: '#15803d', met: '#15803d', paused: '#374151', none: '#64748b',
}
function SlaClock({ label, clock }: { label: string; clock: api.SupportSlaClock }) {
  const color = SLA_TONE[clock.status] ?? SLA_TONE.none
  const fmt = (m: number) => m < 60 ? `${Math.round(m)}m` : m < 60 * 24 ? `${(m / 60).toFixed(1)}h` : `${(m / 60 / 24).toFixed(1)}d`
  return (
    <div style={{ minWidth: 200 }}>
      <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--color-text-mute)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, color, marginTop: 3, textTransform: 'capitalize' }}>{clock.status.replace('_', ' ')}</div>
      {clock.target_minutes !== null && (
        <div style={{ fontSize: 12.5, color: 'var(--color-text-mute)', marginTop: 2 }}>
          {fmt(clock.elapsed_minutes)} of {fmt(clock.target_minutes)}
          {clock.remaining_minutes !== null && clock.remaining_minutes > 0 && (clock.status === 'ok' || clock.status === 'due_soon' || clock.status === 'paused')
            ? ` · ${fmt(clock.remaining_minutes)} left` : ''}
        </div>
      )}
    </div>
  )
}

const wrap: React.CSSProperties = { padding: '24px 24px 48px', maxWidth: '100%', margin: '0 auto', width: '100%' }
const card: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 16 }
const sectionLbl: React.CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: 'var(--color-text-mute)', fontWeight: 600, marginBottom: 10 }
const ctrlLbl: React.CSSProperties = { fontSize: 12.5, color: 'var(--color-text-mute)', fontWeight: 600 }
const backBtn: React.CSSProperties = { background: 'none', border: 'none', color: 'var(--color-text-mute)', cursor: 'pointer', fontSize: 13, padding: 0, marginBottom: 12 }
const ctrlBtn: React.CSSProperties = { padding: '6px 12px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)', fontWeight: 600, fontSize: 13, cursor: 'pointer', textTransform: 'capitalize' }
const primaryBtn: React.CSSProperties = { padding: '8px 16px', borderRadius: 8, border: 'none', background: 'var(--color-primary,#2563eb)', color: '#fff', fontWeight: 600, fontSize: 13.5, cursor: 'pointer' }
const sel: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13, background: 'var(--color-bg)', color: 'var(--color-text)', textTransform: 'capitalize' }
const tag: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '1px 7px', borderRadius: 999 }

/**
 * D-SUPPORT-12 — highlight the "@Name" spans that correspond to REAL stored mentions.
 *
 * Driven by the mention rows, not by pattern-matching "@something" in the text: an email address
 * or a stray "@" in a customer's message must not light up as if a person had been notified.
 * Names are escaped before going into the pattern — a display name is user-controlled text and
 * would otherwise be able to inject regex.
 */
function renderWithMentions(body: string, mentions?: api.SupportCommentMention[]) {
  const names = (mentions ?? []).map(m => m.display_name).filter(Boolean) as string[]
  if (names.length === 0) return body
  // Longest first, so "Yara Shawky" wins over a colleague called "Yara".
  const ordered = [...names].sort((a, b) => b.length - a.length)

  // Deliberately NOT a RegExp built from names. A display name is user-controlled text, so it
  // would have to be regex-escaped first, and the escaping itself is easy to get subtly wrong —
  // the first version of this shipped an escape that replaced every match with itself and quietly
  // escaped nothing. A plain scan has no escaping to get wrong.
  const out: React.ReactNode[] = []
  let i = 0
  let key = 0
  while (i < body.length) {
    if (body[i] === '@') {
      const hit = ordered.find(n => body.startsWith(n, i + 1))
      if (hit) {
        out.push(
          <span key={key++} style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, padding: '0 3px', fontWeight: 600 }}>
            {'@' + hit}
          </span>
        )
        i += hit.length + 1
        continue
      }
    }
    const next = body.indexOf('@', i + 1)
    const stop = next === -1 ? body.length : next
    out.push(body.slice(i, stop))
    i = stop
  }
  return out
}



// ── Close request (D-SUPPORT-5) ─────────────────────────────────────────────
// A customer who has sorted their own problem out previously had no way to say so:
// the ticket sat open burning an SLA clock, or they left a comment and hoped.
// They still cannot CLOSE it — status is platform-only because closing settles an
// SLA measurement and the customer is an interested party — so they ask, and an
// agent confirms.
function CloseRequestCard({
  cr, status, isAdminSurface, canWork, busy, note, setNote, onRequest, onWithdraw, onDecline,
}: {
  cr?: api.SupportCloseRequest
  status: string
  isAdminSurface: boolean
  canWork: boolean
  busy: boolean
  note: string
  setNote: (v: string) => void
  onRequest: () => void
  onWithdraw: () => void
  onDecline: () => void
}) {
  const finished = status === 'closed' || status === 'solved' || status === 'resolved'
  const pending = !!cr?.pending
  const declined = !!cr?.declined_at && !pending

  // Nothing to say once the ticket is done and no request was ever made.
  if (finished && !cr?.requested_at) return null

  const box: React.CSSProperties = {
    border: '1px solid var(--color-border)', borderRadius: 8, padding: 14, marginBottom: 16,
    background: 'var(--color-surface, #fff)',
  }
  const btn: React.CSSProperties = {
    padding: '7px 13px', fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: busy ? 'default' : 'pointer',
    border: '1px solid var(--color-border)', background: 'var(--color-surface, #fff)', color: 'var(--color-text)',
  }
  const input: React.CSSProperties = {
    flex: '1 1 260px', padding: '7px 10px', fontSize: 13, borderRadius: 6,
    border: '1px solid var(--color-border)', background: 'var(--color-bg, #fff)', color: 'var(--color-text)',
  }
  const lbl: React.CSSProperties = {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600,
    color: 'var(--color-text-muted)',
  }

  // ── AGENT SIDE ──
  if (isAdminSurface) {
    if (!pending) return null
    return (
      <div style={{ ...box, borderLeft: '3px solid var(--color-warning, #b45309)' }}>
        <div style={lbl}>Customer asked to close this ticket</div>
        {cr?.note && <div style={{ fontSize: 14, marginTop: 6 }}>“{cr.note}”</div>}
        <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)', marginTop: 6 }}>
          Use <b>Move to → Closed</b> above to accept, or decline if it still needs work.
          Requesting does not change the status or pause the SLA clock.
        </div>
        {canWork && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="Reason for declining (optional)" style={input} />
            <button type="button" style={btn} disabled={busy} onClick={onDecline}>Decline request</button>
          </div>
        )}
      </div>
    )
  }

  // ── CUSTOMER SIDE ──
  if (pending) {
    return (
      <div style={{ ...box, borderLeft: '3px solid var(--color-warning, #b45309)' }}>
        <div style={lbl}>Close requested</div>
        <div style={{ fontSize: 13.5, marginTop: 5 }}>
          A support engineer will confirm before this ticket is closed.
          {cr?.note ? <> Your note: “{cr.note}”</> : null}
        </div>
        <button type="button" style={{ ...btn, marginTop: 10 }} disabled={busy} onClick={onWithdraw}>
          Withdraw request
        </button>
      </div>
    )
  }

  if (declined) {
    return (
      <div style={box}>
        <div style={lbl}>Close request declined</div>
        <div style={{ fontSize: 13.5, marginTop: 5 }}>
          {cr?.decline_reason
            ? <>Support kept this open: “{cr.decline_reason}”</>
            : <>Support kept this ticket open.</>}
        </div>
        {!finished && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <input value={note} onChange={e => setNote(e.target.value)}
              placeholder="Anything to add? (optional)" style={input} />
            <button type="button" style={btn} disabled={busy} onClick={onRequest}>Ask again</button>
          </div>
        )}
      </div>
    )
  }

  if (finished) return null

  return (
    <div style={box}>
      <div style={lbl}>Done with this ticket?</div>
      <div style={{ fontSize: 13.5, marginTop: 5, color: 'var(--color-text-muted)' }}>
        If this is resolved on your side, ask us to close it. A support engineer confirms first.
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <input value={note} onChange={e => setNote(e.target.value)}
          placeholder="Why? e.g. resolved itself (optional)" style={input} />
        <button type="button" style={btn} disabled={busy} onClick={onRequest}>Request to close</button>
      </div>
    </div>
  )
}
